import { FC, ReactNode, useMemo } from "react";
import { Typography, useTheme } from "@mui/material";
import { Modal } from "../Modal";
import { Button } from "components/common/Button";
import SuccessSVG from "images/modals/success.svg";
import WarningSVG from "images/modals/warning.svg";
import InfoSVG from "images/modals/info.svg";

import {
  StyledModalConfirmationModal,
  StyledModalConfirmationModalButtonsContainer,
  StyledModalConfirmationModalContentContainer,
  StyledModalConfirmationModalImg,
  StyledModalConfirmationModalImgContainer,
} from "styles/components/modals";

const modalImages = {
  success: SuccessSVG,
  warning: WarningSVG,
  info: InfoSVG,
};

interface ConfirmationModalProps {
  open: boolean;
  type?: "info" | "success" | "warning";
  children?: ReactNode;
  dataTestId?: string;
  img?: string;
  title?: string;
  text?: string;
  submitBtnText?: string;
  cancelBtnText?: string;

  onClose?: () => void;
  onSubmit?: () => void;
  onCancel?: () => void;
}

const ConfirmationModal: FC<ConfirmationModalProps> = ({
  open,
  type = "success",
  submitBtnText = "Submit button",
  cancelBtnText,
  img,
  children,
  title = "Modal title",
  text = "Modal text",
  onClose,
  onSubmit,
  onCancel,
}) => {
  const theme = useTheme();

  const renderedImage = useMemo(
    () => (img ? img : modalImages[type ?? "success"]),
    [img, type]
  );

  const renderedSubmitBtn = useMemo(
    () =>
      submitBtnText && onSubmit ? (
        <Button onClick={onSubmit}>{submitBtnText}</Button>
      ) : null,
    [onSubmit, submitBtnText]
  );

  const renderedCancelBtn = useMemo(
    () =>
      cancelBtnText && onCancel ? (
        <Button variant="secondary" onClick={onCancel}>
          {cancelBtnText}
        </Button>
      ) : null,
    [cancelBtnText, onCancel]
  );

  return (
    <Modal open={open} onClose={onClose}>
      <StyledModalConfirmationModal className="confirmation-modal">
        <StyledModalConfirmationModalImgContainer>
          <StyledModalConfirmationModalImg
            src={renderedImage}
            alt="modal-img"
          />
        </StyledModalConfirmationModalImgContainer>
        <Typography
          variant="h2"
          textAlign="center"
          color={theme.palette.common.black}
          marginBottom="8px"
        >
          {title}
        </Typography>
        <Typography
          variant="bodyL"
          textAlign="center"
          fontWeight={300}
          color={theme.palette.common.black}
        >
          {text}
        </Typography>
        <StyledModalConfirmationModalContentContainer>
          {children}
        </StyledModalConfirmationModalContentContainer>
        <StyledModalConfirmationModalButtonsContainer>
          {renderedSubmitBtn}
          {renderedCancelBtn}
        </StyledModalConfirmationModalButtonsContainer>
      </StyledModalConfirmationModal>
    </Modal>
  );
};

export default ConfirmationModal;
